import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { Fragment, jsx, jsxs } from "react/jsx-runtime";

//#region src/core/mock.ts
const STORAGE_KEY = "react-openai-apps-sdk-state";
function getStoredState() {
	if (typeof localStorage === "undefined") return {};
	try {
		const stored = localStorage.getItem(STORAGE_KEY);
		return stored ? JSON.parse(stored) : {};
	} catch {
		return {};
	}
}
function saveState(state) {
	if (typeof localStorage === "undefined") return;
	try {
		localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
	} catch (e) {
		console.warn("[OpenAI DevTools] Failed to save state:", e);
	}
}
function createMockOpenAI(config = {}) {
	const stored = getStoredState();
	const state = {
		theme: stored.theme || config.theme || "light",
		displayMode: stored.displayMode || config.displayMode || "inline",
		maxHeight: stored.maxHeight || config.maxHeight || 600,
		locale: stored.locale || config.locale || "en",
		userAgent: stored.userAgent || config.userAgent || {
			device: { type: "desktop" },
			capabilities: {
				hover: true,
				touch: false
			}
		},
		safeArea: stored.safeArea || config.safeArea || { insets: {
			top: 0,
			bottom: 0,
			left: 0,
			right: 0
		} },
		toolInput: stored.toolInput || config.toolInput || null,
		toolOutput: stored.toolOutput || config.toolOutput || null,
		toolResponseMetadata: stored.toolResponseMetadata || config.toolResponseMetadata || null,
		widgetState: stored.widgetState || config.widgetState || null,
		setWidgetState: async (newState) => {
			state.widgetState = newState;
			saveState(state);
			dispatchOpenAIEvent("widget_state_change", { widgetState: newState });
		}
	};
	const mock = {
		...state,
		__devMock: true,
		callTool: async (name, args) => {
			console.log("[OpenAI DevTools] callTool:", name, args);
			return { result: `Mock response for ${name}` };
		},
		sendFollowUpMessage: async (args) => {
			console.log("[OpenAI DevTools] sendFollowUpMessage:", args.prompt);
		},
		openExternal: (payload) => {
			console.log("[OpenAI DevTools] openExternal:", payload.href);
			window.open(payload.href, "_blank");
		},
		requestDisplayMode: async (args) => {
			console.log("[OpenAI DevTools] requestDisplayMode:", args.mode);
			updateMockState("displayMode", args.mode);
			return { mode: args.mode };
		}
	};
	if (typeof document !== "undefined") document.documentElement.classList.toggle("dark", state.theme === "dark");
	return mock;
}
function updateMockState(key, value) {
	if (!window.openai?.__devMock) {
		console.warn("[OpenAI DevTools] Cannot update: not a mock instance");
		return;
	}
	window.openai[key] = value;
	saveState(extractStateFromMock(window.openai));
	if (key === "theme" && typeof document !== "undefined") document.documentElement.classList.toggle("dark", value === "dark");
	dispatchOpenAIEvent("set_globals", { [key]: value });
}
function dispatchOpenAIEvent(type, detail) {
	if (typeof window === "undefined") return;
	window.dispatchEvent(new CustomEvent(`openai:${type}`, { detail: { globals: detail } }));
}
function extractStateFromMock(openai) {
	return {
		theme: openai.theme,
		displayMode: openai.displayMode,
		maxHeight: openai.maxHeight,
		locale: openai.locale,
		userAgent: openai.userAgent,
		safeArea: openai.safeArea,
		toolInput: openai.toolInput,
		toolOutput: openai.toolOutput,
		toolResponseMetadata: openai.toolResponseMetadata,
		widgetState: openai.widgetState
	};
}
function shouldCreateMock(enableMock = true) {
	if (typeof window === "undefined") return false;
	if (!enableMock) return false;
	if (window.openai && !window.openai.__devMock) {
		console.log("[OpenAI DevTools] Real window.openai detected, using native");
		return false;
	}
	if (window.openai?.__devMock) {
		console.log("[OpenAI DevTools] Mock already initialized");
		return false;
	}
	return true;
}

//#endregion
//#region src/components/Toolbar.tsx
function Toolbar({ showHotkeys = true }) {
	const [activeTab, setActiveTab] = useState("controls");
	const [theme, setTheme] = useState("light");
	const [displayMode, setDisplayMode] = useState("inline");
	const [maxHeight, setMaxHeight] = useState(600);
	const [heightInput, setHeightInput] = useState("600");
	const [locale, setLocale] = useState("en");
	const [currentState, setCurrentState] = useState(null);
	useEffect(() => {
		if (typeof window === "undefined" || !window.openai) return;
		const updateState = () => {
			if (!window.openai) return;
			setTheme(window.openai.theme);
			setDisplayMode(window.openai.displayMode);
			setMaxHeight(window.openai.maxHeight);
			setHeightInput(window.openai.maxHeight.toString());
			setLocale(window.openai.locale);
			setCurrentState({
				theme: window.openai.theme,
				displayMode: window.openai.displayMode,
				maxHeight: window.openai.maxHeight,
				locale: window.openai.locale,
				toolInput: window.openai.toolInput,
				toolOutput: window.openai.toolOutput,
				widgetState: window.openai.widgetState,
				toolResponseMetadata: window.openai.toolResponseMetadata,
				userAgent: window.openai.userAgent,
				safeArea: window.openai.safeArea,
				__devMock: window.openai.__devMock || false
			});
		};
		updateState();
		const handleSetGlobals = (event) => {
			const globals = event.detail?.globals;
			if (globals?.theme) setTheme(globals.theme);
			if (globals?.displayMode) setDisplayMode(globals.displayMode);
			if (globals?.maxHeight) {
				setMaxHeight(globals.maxHeight);
				setHeightInput(globals.maxHeight.toString());
			}
			if (globals?.locale) setLocale(globals.locale);
			updateState();
		};
		window.addEventListener("openai:set_globals", handleSetGlobals);
		const interval = setInterval(updateState, 1e3);
		return () => {
			window.removeEventListener("openai:set_globals", handleSetGlobals);
			clearInterval(interval);
		};
	}, []);
	useEffect(() => {
		const handleKeydown = (e) => {
			if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
			if (e.key === "e" || e.key === "E") handleModeToggle(displayMode === "inline" ? "fullscreen" : "inline");
			if (e.key === "t" || e.key === "T") handleThemeToggle(theme === "light" ? "dark" : "light");
		};
		document.addEventListener("keydown", handleKeydown);
		return () => document.removeEventListener("keydown", handleKeydown);
	}, [displayMode, theme]);
	const handleThemeToggle = (newTheme) => {
		const nextTheme = newTheme || (theme === "light" ? "dark" : "light");
		setTheme(nextTheme);
		updateMockState("theme", nextTheme);
	};
	const handleModeToggle = (newMode) => {
		const modes = [
			"inline",
			"fullscreen",
			"pip"
		];
		const nextIndex = (modes.indexOf(displayMode) + 1) % modes.length;
		const nextMode = newMode ?? modes[nextIndex];
		const newMaxHeight = nextMode === "fullscreen" ? window.innerHeight - 100 : nextMode === "pip" ? 400 : 600;
		setDisplayMode(nextMode);
		setMaxHeight(newMaxHeight);
		setHeightInput(newMaxHeight.toString());
		updateMockState("displayMode", nextMode);
		updateMockState("maxHeight", newMaxHeight);
	};
	const handleHeightChange = (e) => {
		setHeightInput(e.target.value);
	};
	const handleHeightSubmit = (e) => {
		e.preventDefault();
		const newHeight = parseInt(heightInput, 10);
		if (!isNaN(newHeight) && newHeight > 0) {
			setMaxHeight(newHeight);
			updateMockState("maxHeight", newHeight);
		}
	};
	return /* @__PURE__ */ jsxs("div", {
		style: {
			background: "rgba(0, 0, 0, 0.9)",
			color: "white",
			padding: "14px 18px",
			borderRadius: "10px",
			fontFamily: "-apple-system, BlinkMacSystemFont, \"Segoe UI\", monospace",
			fontSize: "12px",
			display: "flex",
			flexDirection: "column",
			gap: "10px",
			minWidth: "220px",
			maxWidth: "400px",
			backdropFilter: "blur(10px)"
		},
		children: [
			/* @__PURE__ */ jsxs("div", {
				style: {
					color: "#60a5fa",
					fontSize: "13px",
					marginBottom: "2px"
				},
				children: ["🛠 ", /* @__PURE__ */ jsx("strong", { children: "OpenAI DevTools" })]
			}),
			/* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					gap: "6px",
					borderBottom: "1px solid rgba(255,255,255,0.1)",
					paddingBottom: "8px"
				},
				children: [/* @__PURE__ */ jsx("button", {
					onClick: () => setActiveTab("controls"),
					style: {
						background: activeTab === "controls" ? "rgba(96, 165, 250, 0.3)" : "transparent",
						color: activeTab === "controls" ? "#60a5fa" : "#94a3b8",
						border: "none",
						padding: "6px 12px",
						borderRadius: "4px",
						cursor: "pointer",
						fontSize: "10px",
						fontWeight: "600",
						flex: 1,
						transition: "all 0.2s"
					},
					children: "Controls"
				}), /* @__PURE__ */ jsx("button", {
					onClick: () => setActiveTab("state"),
					style: {
						background: activeTab === "state" ? "rgba(96, 165, 250, 0.3)" : "transparent",
						color: activeTab === "state" ? "#60a5fa" : "#94a3b8",
						border: "none",
						padding: "6px 12px",
						borderRadius: "4px",
						cursor: "pointer",
						fontSize: "10px",
						fontWeight: "600",
						flex: 1,
						transition: "all 0.2s"
					},
					children: "State"
				})]
			}),
			activeTab === "controls" && /* @__PURE__ */ jsxs(Fragment, { children: [
				/* @__PURE__ */ jsxs("button", {
					onClick: () => handleModeToggle(),
					style: {
						background: {
							inline: "#3b82f6",
							fullscreen: "#10b981",
							pip: "#f59e0b"
						}[displayMode],
						color: "white",
						border: "none",
						padding: "7px 14px",
						borderRadius: "6px",
						cursor: "pointer",
						fontSize: "11px",
						fontWeight: "600",
						transition: "opacity 0.2s"
					},
					onMouseOver: (e) => e.currentTarget.style.opacity = "0.8",
					onMouseOut: (e) => e.currentTarget.style.opacity = "1",
					children: [
						{
							inline: "📱",
							fullscreen: "🖥",
							pip: "🪟"
						}[displayMode],
						" ",
						displayMode
					]
				}),
				/* @__PURE__ */ jsx("button", {
					onClick: () => handleThemeToggle(),
					style: {
						background: theme === "light" ? "#f59e0b" : "#6366f1",
						color: "white",
						border: "none",
						padding: "7px 14px",
						borderRadius: "6px",
						cursor: "pointer",
						fontSize: "11px",
						fontWeight: "600",
						transition: "opacity 0.2s"
					},
					onMouseOver: (e) => e.currentTarget.style.opacity = "0.8",
					onMouseOut: (e) => e.currentTarget.style.opacity = "1",
					children: theme === "light" ? "☀️ Light" : "🌙 Dark"
				}),
				/* @__PURE__ */ jsxs("form", {
					onSubmit: handleHeightSubmit,
					style: {
						display: "flex",
						gap: "6px",
						alignItems: "center"
					},
					children: [
						/* @__PURE__ */ jsx("label", {
							style: {
								color: "#94a3b8",
								fontSize: "10px",
								whiteSpace: "nowrap"
							},
							children: "Height (px):"
						}),
						/* @__PURE__ */ jsx("input", {
							type: "number",
							value: heightInput,
							onChange: handleHeightChange,
							style: {
								background: "rgba(255, 255, 255, 0.1)",
								border: "1px solid rgba(255, 255, 255, 0.2)",
								borderRadius: "4px",
								color: "white",
								padding: "4px 8px",
								fontSize: "11px",
								width: "70px"
							},
							min: "100",
							max: "2000"
						}),
						/* @__PURE__ */ jsx("button", {
							type: "submit",
							style: {
								background: "#10b981",
								color: "white",
								border: "none",
								borderRadius: "4px",
								padding: "4px 8px",
								fontSize: "10px",
								cursor: "pointer",
								fontWeight: "600"
							},
							onMouseOver: (e) => e.currentTarget.style.opacity = "0.8",
							onMouseOut: (e) => e.currentTarget.style.opacity = "1",
							children: "Set"
						})
					]
				}),
				/* @__PURE__ */ jsxs("div", {
					style: {
						color: "#94a3b8",
						fontSize: "10px",
						marginTop: "2px"
					},
					children: [
						"Current: ",
						maxHeight,
						"px · ",
						locale
					]
				}),
				showHotkeys && /* @__PURE__ */ jsx("div", {
					style: {
						color: "#64748b",
						fontSize: "9px",
						fontStyle: "italic",
						borderTop: "1px solid rgba(255,255,255,0.1)",
						paddingTop: "6px",
						marginTop: "2px"
					},
					children: "Hotkeys: E=expand, T=theme"
				})
			] }),
			activeTab === "state" && /* @__PURE__ */ jsxs("div", {
				style: {
					display: "flex",
					flexDirection: "column",
					gap: "8px"
				},
				children: [/* @__PURE__ */ jsxs("div", {
					style: {
						color: "#94a3b8",
						fontSize: "10px",
						marginBottom: "4px"
					},
					children: [
						"window.openai state ",
						currentState?.__devMock && "(mock)",
						":"
					]
				}), /* @__PURE__ */ jsx("pre", {
					style: {
						background: "rgba(0, 0, 0, 0.4)",
						padding: "10px",
						borderRadius: "6px",
						fontSize: "9px",
						maxHeight: "300px",
						overflow: "auto",
						margin: 0,
						color: "#e2e8f0",
						lineHeight: "1.4",
						border: "1px solid rgba(255, 255, 255, 0.1)"
					},
					children: currentState ? JSON.stringify(currentState, null, 2) : "No state available"
				})]
			})
		]
	});
}

//#endregion
//#region src/components/OpenAIDevTools.tsx
function OpenAIDevTools({ initialIsOpen = false, buttonPosition = "bottom-right", enableMock = true, mockConfig = {}, showToolbar = true, showHotkeys = true, styleNonce }) {
	const [isOpen, setIsOpen] = useState(initialIsOpen);
	const [isMock, setIsMock] = useState(false);
	useEffect(() => {
		if (process.env.NODE_ENV === "development" && shouldCreateMock(enableMock)) {
			console.log("[OpenAI DevTools] Creating mock window.openai");
			window.openai = createMockOpenAI(mockConfig);
			setIsMock(true);
		} else if (window.openai?.__devMock) setIsMock(true);
		else if (window.openai && !window.openai.__devMock) setIsMock(false);
	}, [enableMock, mockConfig]);
	if (process.env.NODE_ENV === "production" && !import.meta.env.VITE_ENABLE_OPENAI_DEVTOOLS) return null;
	const position = {
		"top-left": {
			top: "20px",
			left: "20px"
		},
		"top-right": {
			top: "20px",
			right: "20px"
		},
		"bottom-left": {
			bottom: "20px",
			left: "20px"
		},
		"bottom-right": {
			bottom: "20px",
			right: "20px"
		}
	}[buttonPosition];
	return /* @__PURE__ */ jsxs("div", {
		style: {
			position: "fixed",
			...position,
			zIndex: 99999
		},
		nonce: styleNonce,
		children: [!isOpen && /* @__PURE__ */ jsxs("button", {
			onClick: () => setIsOpen(true),
			style: {
				background: "rgba(0, 0, 0, 0.9)",
				color: "#60a5fa",
				border: "none",
				borderRadius: "10px",
				padding: "10px 14px",
				cursor: "pointer",
				fontFamily: "-apple-system, BlinkMacSystemFont, \"Segoe UI\", monospace",
				fontSize: "12px",
				fontWeight: "600",
				boxShadow: "0 8px 24px rgba(0, 0, 0, 0.4)",
				backdropFilter: "blur(10px)",
				transition: "opacity 0.2s"
			},
			onMouseOver: (e) => e.currentTarget.style.opacity = "0.8",
			onMouseOut: (e) => e.currentTarget.style.opacity = "1",
			title: "Open OpenAI DevTools",
			children: ["🛠 ", isMock ? "Mock" : "OpenAI"]
		}), isOpen && /* @__PURE__ */ jsxs("div", {
			style: { position: "relative" },
			children: [/* @__PURE__ */ jsx("button", {
				onClick: () => setIsOpen(false),
				style: {
					position: "absolute",
					top: "10px",
					right: "10px",
					background: "rgba(255, 255, 255, 0.1)",
					color: "white",
					border: "none",
					borderRadius: "4px",
					padding: "4px 8px",
					cursor: "pointer",
					fontSize: "10px",
					zIndex: 1
				},
				title: "Close DevTools",
				children: "✕"
			}), showToolbar && /* @__PURE__ */ jsx(Toolbar, { showHotkeys })]
		})]
	});
}

//#endregion
//#region src/components/OpenAIDevToolsPanel.tsx
function OpenAIDevToolsPanel({ style, onClose, showToolbar = true }) {
	const [isMock, setIsMock] = useState(false);
	useEffect(() => {
		if (shouldCreateMock(true)) {
			console.log("[OpenAI DevTools Panel] Creating mock window.openai");
			window.openai = createMockOpenAI();
			setIsMock(true);
		} else if (window.openai?.__devMock) setIsMock(true);
	}, []);
	if (process.env.NODE_ENV === "production") return null;
	return /* @__PURE__ */ jsxs("div", {
		style: {
			height: "500px",
			background: "rgba(0, 0, 0, 0.05)",
			borderRadius: "10px",
			padding: "20px",
			position: "relative",
			overflow: "auto",
			...style
		},
		children: [/* @__PURE__ */ jsxs("div", {
			style: {
				display: "flex",
				justifyContent: "space-between",
				alignItems: "center",
				marginBottom: "20px"
			},
			children: [/* @__PURE__ */ jsxs("div", {
				style: {
					fontFamily: "-apple-system, BlinkMacSystemFont, \"Segoe UI\", monospace",
					fontSize: "14px",
					fontWeight: "600",
					color: "#1f2937"
				},
				children: ["🛠 OpenAI DevTools ", isMock && "(Mock)"]
			}), onClose && /* @__PURE__ */ jsx("button", {
				onClick: onClose,
				style: {
					background: "transparent",
					border: "1px solid #d1d5db",
					borderRadius: "4px",
					padding: "4px 8px",
					cursor: "pointer",
					fontSize: "10px",
					color: "#6b7280"
				},
				children: "Close"
			})]
		}), showToolbar && /* @__PURE__ */ jsx(Toolbar, { showHotkeys: true })]
	});
}

//#endregion
//#region src/hooks/useOpenAIGlobal.ts
function useOpenAIGlobal(key) {
	return useSyncExternalStore(useCallback((callback) => {
		if (typeof window === "undefined") return () => {};
		const handleChange = (event) => {
			if (event.detail?.globals?.[key] !== void 0) callback();
		};
		window.addEventListener("openai:set_globals", handleChange);
		return () => window.removeEventListener("openai:set_globals", handleChange);
	}, [key]), useCallback(() => {
		if (typeof window === "undefined" || !window.openai) return null;
		return window.openai[key] ?? null;
	}, [key]), useCallback(() => null, []));
}

//#endregion
//#region src/components/SafeArea.tsx
/**
* Container that automatically respects safe area insets from mobile devices,
* system UI, and ChatGPT's chat input bar.
*
* WHEN TO USE:
* - Root container for your widget
* - Whenever content should avoid being obscured by device notches or system UI
* - When you need consistent padding on mobile devices
*
* WHEN NOT TO USE:
* - For nested containers (only use at root level)
* - If you're manually managing device-specific layouts
* - For desktop-only widgets that don't need mobile consideration
*
* @see SafeAreaProps for detailed prop documentation
*/
function SafeArea({ children, className = "", style = {}, fallbackHeight = 600, applyInsets = {
	top: true,
	bottom: true,
	left: true,
	right: true
} }) {
	const displayMode = useOpenAIGlobal("displayMode");
	const insets = useOpenAIGlobal("safeArea")?.insets || {
		top: 0,
		bottom: 0,
		left: 0,
		right: 0
	};
	const topInset = applyInsets.top !== false ? insets.top : 0;
	const bottomInset = applyInsets.bottom !== false ? insets.bottom : 0;
	const leftInset = applyInsets.left !== false ? insets.left : 0;
	const rightInset = applyInsets.right !== false ? insets.right : 0;
	const heightStyle = displayMode === "fullscreen" ? `calc(100% - ${bottomInset}px)` : `${fallbackHeight - bottomInset}px`;
	return /* @__PURE__ */ jsx("div", {
		className,
		style: {
			...style,
			height: heightStyle,
			paddingTop: topInset > 0 ? `${topInset}px` : void 0,
			paddingLeft: leftInset > 0 ? `${leftInset}px` : void 0,
			paddingRight: rightInset > 0 ? `${rightInset}px` : void 0,
			boxSizing: "border-box"
		},
		children
	});
}

//#endregion
//#region src/hooks/useOpenAI.ts
function useOpenAI() {
	const [openai, setOpenai] = useState(typeof window !== "undefined" ? window.openai : void 0);
	useEffect(() => {
		if (typeof window === "undefined") return;
		setOpenai(window.openai);
		const handleReady = () => {
			setOpenai(window.openai);
		};
		const handleSetGlobals = () => {
			setOpenai(window.openai);
		};
		window.addEventListener("openai:ready", handleReady);
		window.addEventListener("openai:set_globals", handleSetGlobals);
		return () => {
			window.removeEventListener("openai:ready", handleReady);
			window.removeEventListener("openai:set_globals", handleSetGlobals);
		};
	}, []);
	return openai;
}

//#endregion
//#region src/hooks/useOpenAIActions.ts
/**
* Hook providing safe wrappers for OpenAI API actions
* Handles null checks, error handling, and provides fallback behavior
*/
function useOpenAIActions() {
	const openai = useOpenAI();
	return {
		sendFollowUpMessage: useCallback(async (options) => {
			const { prompt, onSuccess, onError, fallbackMessage } = options;
			try {
				if (!openai?.sendFollowUpMessage) throw new Error(fallbackMessage || "ChatGPT API not available. Please try again later.");
				await openai.sendFollowUpMessage({ prompt });
				onSuccess?.();
				return true;
			} catch (error) {
				const err = error instanceof Error ? error : /* @__PURE__ */ new Error("Unknown error occurred");
				onError?.(err);
				console.error("[useOpenAIActions] sendFollowUpMessage error:", err);
				return false;
			}
		}, [openai]),
		callTool: useCallback(async (options) => {
			const { name, args, onSuccess, onError, fallbackMessage } = options;
			try {
				if (!openai?.callTool) throw new Error(fallbackMessage || "ChatGPT API not available. Please try again later.");
				const typedResult = await openai.callTool(name, args);
				onSuccess?.(typedResult);
				return {
					success: true,
					data: typedResult.structuredContent
				};
			} catch (error) {
				const err = error instanceof Error ? error : /* @__PURE__ */ new Error("Unknown error occurred");
				onError?.(err);
				console.error("[useOpenAIActions] callTool error:", err);
				return { success: false };
			}
		}, [openai]),
		requestDisplayMode: useCallback(async (options) => {
			const { mode, onSuccess, onError, fallbackMessage } = options;
			try {
				if (!openai?.requestDisplayMode) throw new Error(fallbackMessage || "Display mode change not available.");
				const result = await openai.requestDisplayMode({ mode });
				onSuccess?.(result.mode);
				return true;
			} catch (error) {
				const err = error instanceof Error ? error : /* @__PURE__ */ new Error("Unknown error occurred");
				onError?.(err);
				console.error("[useOpenAIActions] requestDisplayMode error:", err);
				return false;
			}
		}, [openai]),
		openExternal: useCallback((options) => {
			const { href, onError, fallbackMessage } = options;
			try {
				if (!openai?.openExternal) throw new Error(fallbackMessage || "External link opening not available.");
				openai.openExternal({ href });
				return true;
			} catch (error) {
				const err = error instanceof Error ? error : /* @__PURE__ */ new Error("Unknown error occurred");
				onError?.(err);
				console.error("[useOpenAIActions] openExternal error:", err);
				return false;
			}
		}, [openai]),
		isAvailable: !!openai
	};
}

//#endregion
//#region src/hooks/useDisplayMode.ts
/**
* Hook to get the current display mode from window.openai
* Based on OpenAI Apps SDK official examples
*
* @returns The current display mode or null if not available
*
* @example
* ```tsx
* function MyWidget() {
*   const displayMode = useDisplayMode();
*
*   return (
*     <div className={displayMode === 'fullscreen' ? 'full' : 'inline'}>
*       {displayMode === 'fullscreen' ? 'Fullscreen View' : 'Inline View'}
*     </div>
*   );
* }
* ```
*/
function useDisplayMode() {
	return useOpenAIGlobal("displayMode");
}

//#endregion
//#region src/hooks/useMaxHeight.ts
/**
* Hook to get the maximum height from window.openai
* Based on OpenAI Apps SDK official examples
*
* @returns The maximum height in pixels or null if not available
*
* @example
* ```tsx
* function MyWidget() {
*   const maxHeight = useMaxHeight();
*
*   return (
*     <div style={{ height: `${maxHeight}px`, overflow: 'auto' }}>
*       Content
*     </div>
*   );
* }
* ```
*/
function useMaxHeight() {
	return useOpenAIGlobal("maxHeight");
}

//#endregion
//#region src/hooks/useWidgetProps.ts
/**
* Hook to get widget properties (toolOutput) from window.openai
* Based on OpenAI Apps SDK official examples
*
* @template T The type of the tool output
* @param defaultState Optional default state if toolOutput is not available
* @returns The tool output data or the default state
*
* @example
* ```tsx
* interface PostData {
*   text: string;
*   images: string[];
* }
*
* function PostWidget() {
*   const post = useWidgetProps<PostData>({ text: '', images: [] });
*
*   return (
*     <div>
*       <p>{post.text}</p>
*       {post.images.map(img => <img key={img} src={img} />)}
*     </div>
*   );
* }
* ```
*/
function useWidgetProps(defaultState) {
	const props = useOpenAIGlobal("toolOutput");
	const fallback = typeof defaultState === "function" ? defaultState() : defaultState ?? null;
	return props ?? fallback;
}

//#endregion
//#region src/hooks/useWidgetState.ts
function useWidgetState(defaultState) {
	const widgetStateFromWindow = useOpenAIGlobal("widgetState");
	const [widgetState, _setWidgetState] = useState(() => {
		if (widgetStateFromWindow != null) return widgetStateFromWindow;
		return typeof defaultState === "function" ? defaultState() : defaultState ?? null;
	});
	useEffect(() => {
		_setWidgetState(widgetStateFromWindow);
	}, [widgetStateFromWindow]);
	return [widgetState, useCallback((state) => {
		_setWidgetState((prevState) => {
			const newState = typeof state === "function" ? state(prevState) : state;
			if (newState != null && typeof window !== "undefined" && window.openai) window.openai.setWidgetState(newState);
			return newState;
		});
	}, [])];
}

//#endregion
export { OpenAIDevTools, OpenAIDevToolsPanel, SafeArea, createMockOpenAI, dispatchOpenAIEvent, getStoredState, saveState, shouldCreateMock, updateMockState, useDisplayMode, useMaxHeight, useOpenAI, useOpenAIActions, useOpenAIGlobal, useWidgetProps, useWidgetState };
//# sourceMappingURL=index.js.map